import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDownIcon,
  ArrowPathIcon,
  ArrowUpIcon,
  BuildingLibraryIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  CpuChipIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';
import { ModalShell } from './core';
import Combobox from '../Combobox';

export interface UncategorizedTransaction {
  id: number;
  buchungstag: string;
  buchungstext: string;
  name_zahlungsbeteiligter?: string | null;
  verwendungszweck?: string | null;
  categoryAmount?: number;
  isIncome?: boolean;
  konto_name?: string;
}

interface AiCategory {
  id: number;
  name: string;
  beschreibung?: string | null;
  requires_member?: boolean;
}

interface AiSuggestion {
  statement_id: number;
  category_id?: number;
  category_name?: string;
  category_description?: string | null;
  confidence?: number;
  needs_review?: boolean;
  reason?: string;
  signals?: string[];
  error?: string;
  member_id?: number | null;
  member_confidence?: number | null;
  member_reason?: string;
  beitrag_jahr?: number;
  year_needs_review?: boolean;
  year_reason?: string;
}

interface Props {
  open: boolean;
  title?: string;
  summary?: string;
  autoAnalyze?: boolean;
  year: number;
  transactions: UncategorizedTransaction[];
  loading?: boolean;
  onClose: () => void;
  onOpenStatement: (statementId: number) => void;
  onApplied: () => void | Promise<void>;
}

const BATCH_SIZE = 40;

const euro = (value: number) => new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
}).format(value || 0);

const datum = (value: string) => new Date(value).toLocaleDateString('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const kopf = (transaction: UncategorizedTransaction) =>
  transaction.name_zahlungsbeteiligter?.trim() || transaction.buchungstext || 'Ohne Angabe';

const zweck = (transaction: UncategorizedTransaction) => {
  const text = transaction.verwendungszweck?.trim() || '';
  return text.replace(/\s(?:EREF|MREF|CRED|SVWZ|ABWA|ABWE)[+:].*/i, '').trim() || text;
};

const confidenceStyle = (confidence = 0) => {
  if (confidence >= 80) return 'border-success-200 bg-success-50 text-success-700 dark:border-success-800 dark:bg-success-900/20 dark:text-success-300';
  if (confidence >= 60) return 'border-warning-200 bg-warning-50 text-warning-700 dark:border-warning-800 dark:bg-warning-900/20 dark:text-warning-300';
  return 'border-gray-200 bg-gray-100 text-gray-600 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300';
};

const UncategorizedReviewModal: React.FC<Props> = ({
  open,
  year,
  transactions: inputTransactions,
  title = "Offene Buchungen prüfen",
  autoAnalyze = false,
  summary,
  loading = false,
  onClose,
  onOpenStatement,
  onApplied,
}) => {
  const [completed, setCompleted] = useState<Set<number>>(new Set());
  const transactions = useMemo(() => inputTransactions.filter(transaction => !completed.has(transaction.id)), [inputTransactions, completed]);
  const [members, setMembers] = useState<{ member_id: number; vorname: string; name: string; geloescht?: number }[]>([]);
  const generation = useRef(0);
  const autoStarted = useRef(false);
  const [suggestions, setSuggestions] = useState<Record<number, AiSuggestion>>({});
  const [categories, setCategories] = useState<AiCategory[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState('');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    generation.current += 1;
    autoStarted.current = false;
    if (!open) return;
    setCompleted(new Set());
    setSuggestions({});
    setCategories([]);
    setSelected(new Set());
    setAnalyzing(false);
    setAnalysisProgress('');
    setApplying(false);
    setError(null);
    setSuccess(null);
    return () => { generation.current += 1; };
  }, [open, year]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    apiClient.get('/api/finance/members').then(response => {
      if (!cancelled) setMembers(response.data);
    }).catch(() => { if (!cancelled) setError('Mitglieder konnten nicht geladen werden. Bitte die Ansicht erneut öffnen.'); });
    return () => { cancelled = true; };
  }, [open]);

  const totals = useMemo(() => {
    const income = transactions
      .filter((transaction) => transaction.isIncome)
      .reduce((sum, transaction) => sum + Math.abs(Number(transaction.categoryAmount) || 0), 0);
    const expense = transactions
      .filter((transaction) => !transaction.isIncome)
      .reduce((sum, transaction) => sum + Math.abs(Number(transaction.categoryAmount) || 0), 0);
    return { income, expense, open: income + expense };
  }, [transactions]);

  const categoryById = useMemo(
    () => new Map(categories.map((category) => [Number(category.id), category])),
    [categories]
  );

  const isApplyable = (suggestion?: AiSuggestion) => {
    if (!suggestion?.category_id || suggestion.error) return false;
    const category = categoryById.get(Number(suggestion.category_id));
    if (!category) return false;
    return !category.requires_member || Boolean(
      members.some(member => Number(member.member_id) === Number(suggestion.member_id)) &&
      Number.isInteger(suggestion.beitrag_jahr) && Number(suggestion.beitrag_jahr) >= 1900 && Number(suggestion.beitrag_jahr) <= 2100
    );
  };

  const applyableIds = transactions
    .map((transaction) => transaction.id)
    .filter((id) => isApplyable(suggestions[id]));
  const selectedCount = applyableIds.filter((id) => selected.has(id)).length;
  const allSelected = applyableIds.length > 0 && selectedCount === applyableIds.length;
  const hasSuggestions = Object.keys(suggestions).length > 0;

  const analyze = async () => {
    if (!transactions.length || applying || analyzing) return;
    const token = ++generation.current;
    setAnalyzing(true);
    setError(null);
    setSuccess(null);
    setSelected(new Set());

    try {
      const combined: Record<number, AiSuggestion> = {};
      let loadedCategories: AiCategory[] = [];
      const chunks: UncategorizedTransaction[][] = [];
      for (let index = 0; index < transactions.length; index += BATCH_SIZE) {
        chunks.push(transactions.slice(index, index + BATCH_SIZE));
      }

      for (let index = 0; index < chunks.length; index += 1) {
        setAnalysisProgress(chunks.length > 1 ? `Paket ${index + 1} von ${chunks.length}` : 'Buchungen werden gemeinsam geprüft');
        const response = await apiClient.post('/api/finance/statements/suggest-categories', {
          statement_ids: chunks[index].map((transaction) => transaction.id),
        }, {
          // Strukturierte Vorschlaege fuer viele Buchungen koennen laenger als
          // der globale API-Standard von 30 Sekunden benoetigen.
          timeout: 180000,
        });
        if (token !== generation.current) return;
        if (!response.data?.success) {
          throw new Error(response.data?.error || 'Die KI hat keine Vorschläge geliefert.');
        }
        loadedCategories = response.data.categories || loadedCategories;
        for (const suggestion of response.data.suggestions || []) {
          const transaction = transactions.find(item => item.id === Number(suggestion.statement_id));
          combined[Number(suggestion.statement_id)] = { ...suggestion,
            beitrag_jahr: suggestion.beitrag_jahr || Number(transaction?.buchungstag.slice(0, 4)) || year,
          };
        }
        setCategories(loadedCategories);
        setSuggestions({ ...combined });
      }

      setCategories(loadedCategories);
      setSuggestions(combined);
      const categoryMap = new Map(loadedCategories.map((category) => [Number(category.id), category]));
      setSelected(new Set(
        Object.values(combined)
          .filter((suggestion) =>
            suggestion.category_id &&
            !suggestion.error &&
            (!categoryMap.get(Number(suggestion.category_id))?.requires_member ||
              (suggestion.member_id && Number(suggestion.member_confidence) >= 85 && !suggestion.year_needs_review)) &&
            Number(suggestion.confidence || 0) >= 70 &&
            !suggestion.needs_review
          )
          .map((suggestion) => suggestion.statement_id)
      ));
    } catch (err: any) {
      if (token !== generation.current) return;
      console.error('UncategorizedReviewModal: KI-Sammelanalyse fehlgeschlagen', err);
      const timeout = err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT';
      setError(
        timeout
          ? 'Die KI-Analyse hat länger als drei Minuten gedauert. Bitte erneut versuchen oder weniger Buchungen gemeinsam analysieren.'
          : err?.response?.data?.error || err?.message || 'Die KI-Sammelanalyse ist fehlgeschlagen.'
      );
    } finally {
      if (token === generation.current) { setAnalyzing(false); setAnalysisProgress(''); }
    }
  };

  useEffect(() => {
    if (open && autoAnalyze && transactions.length && !autoStarted.current) {
      autoStarted.current = true;
      void analyze();
    }
    // The analysis starts once per opening; later edits must preserve the selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoAnalyze, transactions.length]);

  const toggleSelection = (id: number) => {
    if (!isApplyable(suggestions[id])) return;
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(applyableIds));
  };

  const changeCategory = (statementId: number, categoryId: number) => {
    const category = categoryById.get(categoryId);
    if (!category) return;
    const transaction = transactions.find(item => item.id === statementId);
    setSuggestions((current) => ({
      ...current,
      [statementId]: {
        ...current[statementId],
        statement_id: statementId,
        category_id: category.id,
        category_name: category.name,
        category_description: category.beschreibung || null,
        beitrag_jahr: current[statementId]?.beitrag_jahr || Number(transaction?.buchungstag.slice(0, 4)) || year,
      },
    }));
    setSelected((current) => {
      const next = new Set(current);
      if (category.requires_member) next.delete(statementId);
      else next.add(statementId);
      return next;
    });
  };

  const applySelected = async (onlyId?: number) => {
    if (applying || analyzing) return;
    const applyableSet = new Set(applyableIds);
    const assignments = (onlyId ? [onlyId] : Array.from(selected))
      .filter((statementId) => applyableSet.has(statementId))
      .map((statementId) => ({
        statement_id: statementId,
        category_id: suggestions[statementId]?.category_id,
        member_id: suggestions[statementId]?.member_id,
        beitrag_jahr: suggestions[statementId]?.beitrag_jahr,
      }))
      .filter((assignment) => assignment.category_id);
    if (!assignments.length) return;

    setApplying(true);
    setError(null);
    setSuccess(null);
    try {
      let count = 0, skipped = 0;
      for (let offset = 0; offset < assignments.length; offset += 100) {
        const response = await apiClient.post('/api/finance/statements/apply-category-suggestions', { assignments: assignments.slice(offset, offset + 100) });
        if (!response.data?.success) throw new Error(response.data?.error || 'Die Zuordnungen konnten nicht gespeichert werden.');
        const appliedIds = new Set<number>((response.data.applied || []).map((item: any) => Number(item.statement_id)));
        setCompleted(current => new Set([...Array.from(current), ...Array.from(appliedIds)]));
        setSelected(current => new Set(Array.from(current).filter(id => !appliedIds.has(id))));
        count += response.data.applied_count || 0;
        skipped += response.data.skipped?.length || 0;
        setSuccess(`${count} Buchung${count === 1 ? '' : 'en'} übernommen.`);
      }
      if (skipped) setError(`${skipped} Zuordnungen benötigen eine manuelle Prüfung. Bitte Vorschläge neu laden.`);
      await onApplied();
    } catch (err: any) {
      console.error('UncategorizedReviewModal: Übernahme fehlgeschlagen', err);
      setError(err?.response?.data?.error || err?.message || 'Die Zuordnungen konnten nicht gespeichert werden.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={() => { if (!applying) onClose(); }}
      variant="detail"
      size="xl"
      mobilePresentation="fullscreen"
      icon={SparklesIcon}
      iconTone="warning"
      title={title}
      subtitle={`${transactions.length} Buchungen · ${euro(totals.open)} offen`}
      bodyClassName="bg-gray-50/70 dark:bg-[#111821]"
      footer={
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-300">
            {hasSuggestions ? (
              <><strong className="text-gray-900 dark:text-white">{selectedCount}</strong> von {applyableIds.length} Vorschlägen ausgewählt</>
            ) : (
              'Noch keine Kategorien vorgeschlagen'
            )}
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <button
              type="button"
              onClick={onClose}
              disabled={applying}
              className="min-h-[44px] rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Schließen
            </button>
            <button
              type="button"
              onClick={() => applySelected()}
              disabled={!selectedCount || applying || analyzing}
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-primary-600 px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-45"
            >
              {applying ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckCircleIcon className="h-4 w-4" />}
              {applying ? 'Wird übernommen …' : `${selectedCount || ''} ${selectedCount === 1 ? 'Zuordnung' : 'Zuordnungen'} übernehmen`}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {summary && <p className="rounded-xl border border-primary-200 bg-primary-50 p-3 text-sm text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-200">{summary}</p>}
        <section className="grid grid-cols-3 overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="border-r border-gray-200 p-3.5 dark:border-gray-700">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Offen</p>
            <p className="mt-1 text-lg font-bold tabular-nums text-gray-900 dark:text-white">{euro(totals.open)}</p>
          </div>
          <div className="border-r border-gray-200 p-3.5 dark:border-gray-700">
            <p className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-success-600 dark:text-success-400"><ArrowUpIcon className="h-3 w-3" /> Einnahmen</p>
            <p className="mt-1 text-lg font-bold tabular-nums text-success-700 dark:text-success-300">{euro(totals.income)}</p>
          </div>
          <div className="p-3.5">
            <p className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-error-600 dark:text-error-400"><ArrowDownIcon className="h-3 w-3" /> Ausgaben</p>
            <p className="mt-1 text-lg font-bold tabular-nums text-error-700 dark:text-error-300">{euro(totals.expense)}</p>
          </div>
        </section>

        <section className="rounded-xl border border-primary-200 bg-primary-50 p-4 dark:border-primary-800 dark:bg-primary-900/20">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-primary-200 bg-white text-primary-600 dark:border-primary-700 dark:bg-gray-800 dark:text-primary-400">
                <CpuChipIcon className="h-5 w-5" />
              </span>
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white">Kategorien und Mitglieder vorschlagen</h3>
                <p className="mt-0.5 max-w-2xl text-xs leading-relaxed text-gray-600 dark:text-gray-300">
                  Die KI schlägt passende Kategorien vor. Bei Mitgliedsbeiträgen werden Mitglied und Beitragsjahr
                  vorbefüllt, soweit sie erkennbar sind. Unsichere Zuordnungen bleiben zur Prüfung offen.
                  Du kannst jede Zeile ändern, einzeln übernehmen oder abwählen. Gespeichert wird erst nach deiner Bestätigung.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={analyze}
              disabled={analyzing || applying || loading || transactions.length === 0}
              className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {analyzing ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <SparklesIcon className="h-4 w-4" />}
              {analyzing ? (analysisProgress || 'KI analysiert …') : `${transactions.length} Buchungen analysieren`}
            </button>
          </div>
        </section>

        {(error || success) && (
          <div className="space-y-2" aria-live="polite">
            {success && (
              <div className="flex items-start gap-2 rounded-lg border border-success-200 bg-success-50 p-3 text-sm text-success-800 dark:border-success-800 dark:bg-success-900/20 dark:text-success-200">
                <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0" /> {success}
              </div>
            )}
            {error && (
              <div className="flex items-start gap-2 rounded-lg border border-warning-200 bg-warning-50 p-3 text-sm text-warning-800 dark:border-warning-800 dark:bg-warning-900/20 dark:text-warning-200">
                <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" /> {error}
              </div>
            )}
          </div>
        )}

        {hasSuggestions && applyableIds.length > 0 && (
          <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <ShieldCheckIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              Hohe Sicherheit ist vorausgewählt; jede Zuordnung bleibt änderbar.
            </div>
            <button type="button" onClick={toggleAll} disabled={analyzing || applying} className="min-h-[36px] rounded-md px-3 text-xs font-bold text-primary-700 hover:bg-primary-50 dark:text-primary-300 dark:hover:bg-primary-900/20">
              {allSelected ? 'Auswahl aufheben' : 'Alle auswählen'}
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500 dark:text-gray-400">
            <ArrowPathIcon className="h-7 w-7 animate-spin text-primary-600" />
            <p className="mt-3 text-sm">Offene Buchungen werden geladen …</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="rounded-xl border border-success-200 bg-success-50 px-5 py-12 text-center dark:border-success-800 dark:bg-success-900/20">
            <CheckCircleIcon className="mx-auto h-10 w-10 text-success-600 dark:text-success-400" />
            <h3 className="mt-3 font-bold text-success-900 dark:text-success-100">Alles kategorisiert</h3>
            <p className="mt-1 text-sm text-success-700 dark:text-success-300">Alle Buchungen dieser Auswahl sind kategorisiert.</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {transactions.map((transaction) => {
              const suggestion = suggestions[transaction.id];
              const category = suggestion?.category_id ? categoryById.get(Number(suggestion.category_id)) : undefined;
              const requiresMember = Boolean(category?.requires_member);
              const checked = selected.has(transaction.id);
              return (
                <article
                  key={transaction.id}
                  className={`rounded-xl border bg-white p-3.5 transition-colors dark:bg-gray-800 ${
                    checked
                      ? 'border-primary-300 ring-1 ring-primary-100 dark:border-primary-700 dark:ring-primary-900/30'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="grid gap-3 md:grid-cols-[auto_minmax(0,1.35fr)_minmax(230px,1fr)_auto] md:items-center">
                    <div className="flex items-start md:items-center">
                      {suggestion ? (
                        <input
                          type="checkbox"
                          aria-label={`Zuordnung für ${kopf(transaction)} auswählen`}
                          checked={checked}
                          disabled={analyzing || applying || !isApplyable(suggestion)}
                          onChange={() => toggleSelection(transaction.id)}
                          className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-35"
                        />
                      ) : (
                        <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${transaction.isIncome ? 'bg-success-50 text-success-600 dark:bg-success-900/20 dark:text-success-400' : 'bg-error-50 text-error-600 dark:bg-error-900/20 dark:text-error-400'}`}>
                          {transaction.isIncome ? <ArrowUpIcon className="h-4 w-4" /> : <ArrowDownIcon className="h-4 w-4" />}
                        </span>
                      )}
                    </div>

                    <div className="min-w-0">
                      <h4 className="truncate text-sm font-bold text-gray-900 dark:text-white">{kopf(transaction)}</h4>
                      {zweck(transaction) && (
                        <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-gray-600 dark:text-gray-300" title={transaction.verwendungszweck || undefined}>
                          {zweck(transaction)}
                        </p>
                      )}
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
                        <span className="inline-flex items-center gap-1"><CalendarDaysIcon className="h-3.5 w-3.5" />{datum(transaction.buchungstag)}</span>
                        <span className="inline-flex items-center gap-1"><BuildingLibraryIcon className="h-3.5 w-3.5" />{transaction.konto_name || 'Ohne Konto'}</span>
                        {transaction.buchungstext && transaction.buchungstext !== kopf(transaction) && <span>{transaction.buchungstext}</span>}
                      </div>
                    </div>

                    <div className="min-w-0">
                      {suggestion?.error ? (
                        <div className="flex items-start gap-2 rounded-lg border border-warning-200 bg-warning-50 p-2.5 text-xs text-warning-800 dark:border-warning-800 dark:bg-warning-900/20 dark:text-warning-200">
                          <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0" /> {suggestion.error}
                        </div>
                      ) : suggestion ? (
                        <div>
                          <div className="flex items-center gap-2">
                            <select
                              aria-label={`Kategorie für ${kopf(transaction)}`}
                              disabled={analyzing || applying}
                              value={suggestion.category_id || ''}
                              onChange={(event) => changeCategory(transaction.id, Number(event.target.value))}
                              className="min-h-[40px] min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-[#111821] dark:text-white"
                            >
                              {categories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                            </select>
                            <span className={`shrink-0 rounded-md border px-2 py-1 text-[11px] font-bold tabular-nums ${confidenceStyle(suggestion.confidence)}`}>
                              {suggestion.confidence || 0}%
                            </span>
                          </div>
                          {suggestion.reason && <p className="mt-1.5 line-clamp-2 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">{suggestion.reason}</p>}
                          {requiresMember && (
                            <div className="mt-3 space-y-2 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900">
                              <Combobox
                                label={`Mitglied für ${kopf(transaction)}`}
                                placeholder="Mitglied auswählen …"
                                disabled={applying || analyzing}
                                value={suggestion.member_id || null}
                                options={members.map(member => ({ id: member.member_id, label: `${member.vorname} ${member.name}${member.geloescht ? ' (ausgeschieden)' : ''}`, hint: `Mitglied Nr. ${member.member_id}` }))}
                                onChange={id => setSuggestions(current => ({ ...current, [transaction.id]: { ...current[transaction.id], member_id: Number(id), member_reason: 'Mitglied manuell ausgewählt.' } }))}
                              />
                              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300">
                                Beitragsjahr
                                <input type="number" min={1900} max={2100} step={1}
                                  aria-label={`Beitragsjahr für ${kopf(transaction)}`}
                                  disabled={applying || analyzing}
                                  value={suggestion.beitrag_jahr || ''}
                                  onChange={event => setSuggestions(current => ({ ...current, [transaction.id]: { ...current[transaction.id], beitrag_jahr: Number(event.target.value), year_needs_review: false } }))}
                                  className="mt-1 min-h-[40px] w-full rounded-lg border border-gray-300 bg-white px-3 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white" />
                              </label>
                              <p className="text-[11px] text-gray-500 dark:text-gray-400">{suggestion.member_reason || 'Bitte Mitglied auswählen.'}{suggestion.member_confidence ? ` (${suggestion.member_confidence} %)` : ''} {suggestion.year_reason}</p>
                              {!isApplyable(suggestion) && <p className="text-xs font-semibold text-warning-700 dark:text-warning-300">Mitglied und gültiges Beitragsjahr sind erforderlich.</p>}
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 dark:text-gray-500">Noch kein KI-Vorschlag</p>
                      )}
                    </div>

                    <div className="flex items-center justify-between gap-3 md:justify-end">
                      <span className={`whitespace-nowrap text-sm font-bold tabular-nums ${transaction.isIncome ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`}>
                        {transaction.isIncome ? '+' : '−'}{euro(Math.abs(Number(transaction.categoryAmount) || 0))}
                      </span>
                      {suggestion && <button type="button"
                        onClick={() => applySelected(transaction.id)}
                        disabled={applying || analyzing || !isApplyable(suggestion)}
                        aria-label={`Zuordnung für ${kopf(transaction)} übernehmen`}
                        className="min-h-[40px] rounded-lg bg-primary-600 px-3 text-xs font-bold text-white hover:bg-primary-700 disabled:opacity-40">
                        Übernehmen
                      </button>}
                      <button
                        type="button"
                        disabled={applying || analyzing}
                        onClick={() => onOpenStatement(transaction.id)}
                        aria-label={`Buchung ${kopf(transaction)} im Detail öffnen`}
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 dark:border-gray-600 dark:text-gray-400 dark:hover:border-primary-700 dark:hover:bg-primary-900/20 dark:hover:text-primary-300"
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </ModalShell>
  );
};

export default UncategorizedReviewModal;
